/* =============================================================
   ISD cart state — the one place that decides, from the real Shopify cart:
     - whether a qualifying box is in the cart (product type or tag)
     - which lines are box add-ons (special-price duplicates)
     - the cart weight (Shopify grams × quantity, via cart.total_weight)
     - the delivery state (€45 locker / 10 kg heavy / €75 free for everything)
     - add-on validation: no box → add-on lines are removed, customer is told

   Loaded once from layout/theme.liquid right after the ISD cart API.
   Thresholds come from window.ISD.config (theme settings, one source of truth).

   API
     ISD.state.compute(cart)        -> state (sync; uses cached classifications)
     ISD.state.resolve(cart)        -> Promise<state> (classifies unknown lines first)
     ISD.state.validate()           -> Promise<state> (re-fetches the cart, removes
                                       invalid add-ons, resolves with the final state)
     ISD.state.subscribe(fn)        -> fn(state, cart) on every cart change; returns unsubscribe
     ISD.state.last()               -> last state or null
     ISD.state.fmtWeight(grams)     -> "850 g" / "1,25 kg" / "10 kg"
     ISD.state.text                 -> customer copy (overridable from Liquid)
     ISD.state.renderShip(state, opts) -> HTML of the compact free-shipping progress (locker + courier milestones);
                                       opts { weight: bool, weightLabel: str } adds one small weight line
     ISD.state.shipText(state)      -> the same message as plain text (live regions, tests)
     ISD.visibility.kind(p)         -> hidden | box | offer | gift | product (JS twin of snippets/isd-disc-kind.liquid)
     ISD.visibility.isHidden(p)     -> true for the tasting sets and any non-box "Dėžutės" product
   Events: document "isd:state" with detail { state, cart, action }
   ============================================================= */
(function () {
  'use strict';
  if (!window.ISD || !window.ISD.cart || window.ISD.state) return;

  var C = window.ISD.config || {};
  var cfg = {
    lockerFreeFrom: Number(C.lockerFreeFrom) || 4500,   /* cents */
    freeAllFrom: Number(C.freeAllFrom) || 7500,         /* cents */
    heavyGrams: Number(C.heavyGrams) || 10000,          /* grams, inclusive */
    courierPrice: Number(C.courierPrice) || 350,        /* cents */
    lockerPrice: Number(C.lockerPrice) || 245,          /* cents */
    boxTag: norm(C.boxTag || 'dezute'),
    boxType: norm(C.boxType || 'Dėžutės'),
    addonTag: norm(C.addonTag || 'box-addon'),
    addonType: norm(C.addonType || 'Dėžutės priedas'),
    addonHandleSuffix: norm(C.addonHandleSuffix || '-prie-dezutes')
  };

  var text = window.ISD.text || {};

  /* Final shipping-message wording, fixed and locale-correct here rather than trusting
     window.ISD.text: theme.liquid seeds window.ISD.text from the (Lithuanian) theme
     settings before the locale-specific block runs, and on en/lv that non-empty LT
     text wins the Object.assign merge — so the translated defaults never actually
     applied. Keying off <html lang> sidesteps that bug entirely. */
  var htmlLang = (document.documentElement.lang || 'lt').slice(0, 2).toLowerCase();
  var SHIP_COPY = {
    lt: {
      state1: 'Nemokamas pristatymas į paštomatą nuo {locker}.',
      state2: 'Jums priklauso nemokamas pristatymas į paštomatą.',
      state3: 'Dėl užsakymo svorio galimas pristatymas tik kurjeriu.',
      state4: 'Jums priklauso nemokamas pristatymas kurjeriu.',
      toLocker: 'Dar {amount} iki nemokamo paštomato',
      lockerOpen: 'Nemokamas paštomatas atrakintas',
      toCourier: 'Dar {amount} iki nemokamo kurjerio',
      courierOpen: 'Nemokamas kurjeris atrakintas',
      heavyOnly: 'Tik kurjeris (svoris virš {kg})',
      labelLocker: 'Paštomatas', labelCourier: 'Kurjeris', weight: 'Svoris'
    },
    lv: {
      state1: 'Bezmaksas piegāde uz pakomātu no {locker}.',
      state2: 'Jums pienākas bezmaksas piegāde uz pakomātu.',
      state3: 'Pasūtījuma svara dēļ pieejama tikai kurjera piegāde.',
      state4: 'Jums pienākas bezmaksas piegāde ar kurjeru.',
      toLocker: 'Vēl {amount} līdz bezmaksas pakomātam',
      lockerOpen: 'Bezmaksas pakomāts atbloķēts',
      toCourier: 'Vēl {amount} līdz bezmaksas kurjeram',
      courierOpen: 'Bezmaksas kurjers atbloķēts',
      heavyOnly: 'Tikai kurjers (svars virs {kg})',
      labelLocker: 'Pakomāts', labelCourier: 'Kurjers', weight: 'Svars'
    },
    en: {
      state1: 'Free parcel-locker delivery from {locker}.',
      state2: 'You qualify for free parcel-locker delivery.',
      state3: 'Due to the order weight, only courier delivery is available.',
      state4: 'You qualify for free courier delivery.',
      toLocker: '{amount} to go for a free parcel locker',
      lockerOpen: 'Free parcel locker unlocked',
      toCourier: '{amount} to go for free courier',
      courierOpen: 'Free courier unlocked',
      heavyOnly: 'Courier only (weight over {kg})',
      labelLocker: 'Parcel locker', labelCourier: 'Courier', weight: 'Weight'
    }
  };
  var SHIP = SHIP_COPY[htmlLang] || SHIP_COPY.en;

  var T = {
    lockerLabel: text.lockerLabel || 'Paštomatas',
    courierLabel: text.courierLabel || 'Kurjeris',
    state1: SHIP.state1.replace('{locker}', fmtThreshold(cfg.lockerFreeFrom)),
    state2: SHIP.state2,
    state2courier: text.state2courier || 'Kurjeris — {price}',
    state2more: text.state2more || 'Iki nemokamo kurjerio liko {amount}',
    state3: SHIP.state3,
    state3courier: text.state3courier || 'Kurjeris — {price}',
    state3more: text.state3more || 'Iki nemokamo pristatymo liko {amount}',
    state4: SHIP.state4,
    state4heavy: text.state4heavy || 'Nemokamai pristatysime kurjeriu.',
    weightLabel: text.weightLabel || 'Krepšelio svoris',
    addonRemoved: text.addonRemoved || 'Specialios kainos prekėms reikalinga skonio dėžutė. Prekės specialia kaina pašalintos iš krepšelio.',
    addonBlocked: text.addonBlocked || 'Specialios kainos prekėms reikalinga skonio dėžutė.'
  };

  function norm(s) { return String(s || '').toLowerCase().normalize('NFD').replace(/\p{M}/gu, '').trim(); }
  function money(c) { return window.ISD.money(c); }
  function fmtWeight(grams) {
    grams = Math.max(0, Math.round(Number(grams) || 0));
    if (grams < 1000) return grams + ' g';
    var kg = grams / 1000;
    var s = (Math.round(kg * 100) / 100).toFixed(2).replace(/\.?0+$/, '');
    return s.replace('.', ',') + ' kg';
  }

  /* ---------- Line classification (box / addon / other) ---------- */
  var cache = {};          /* product_id -> { box, addon } */
  var pending = {};        /* product_id -> Promise */

  function classifyFrom(type, tags, handle) {
    var t = norm(type), h = norm(handle);
    var tg = (tags || []).map(norm);
    var isBoxType = t && (t === cfg.boxType || t === 'dezutes' || t === 'boxes');
    var isAddonType = t && (t === cfg.addonType || t === 'dezutes priedas' || t === 'box add-on');
    var box = isBoxType || tg.indexOf(cfg.boxTag) > -1;
    var addon = isAddonType || tg.indexOf(cfg.addonTag) > -1 || (cfg.addonHandleSuffix && h.slice(-cfg.addonHandleSuffix.length) === cfg.addonHandleSuffix);
    return { box: !!box && !addon, addon: !!addon };
  }
  function quick(item) {
    /* cart.js has product_type and handle but no tags; decide what we can without a request */
    var t = norm(item.product_type), h = norm(item.handle || '');
    var isBoxType = t && (t === cfg.boxType || t === 'dezutes' || t === 'boxes');
    var isAddonType = t && (t === cfg.addonType || t === 'dezutes priedas' || t === 'box add-on');
    if (isAddonType) return { box: false, addon: true };
    if (cfg.addonHandleSuffix && h.slice(-cfg.addonHandleSuffix.length) === cfg.addonHandleSuffix) return { box: false, addon: true };
    if (isBoxType) return { box: true, addon: false };
    return null;
  }
  function classify(item) {
    var id = item.product_id;
    if (cache[id]) return cache[id];
    var q = quick(item);
    if (q) { cache[id] = q; return q; }
    return null;
  }
  function fetchClass(item) {
    var id = item.product_id;
    if (pending[id]) return pending[id];
    pending[id] = fetch('/products/' + item.handle + '.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) { cache[id] = p ? classifyFrom(p.type, p.tags, p.handle) : { box: false, addon: false }; })
      .catch(function () { cache[id] = { box: false, addon: false }; })
      .then(function () { delete pending[id]; });
    return pending[id];
  }
  function resolve(cart) {
    var jobs = [];
    (cart.items || []).forEach(function (it) { if (!classify(it)) jobs.push(fetchClass(it)); });
    return Promise.all(jobs).then(function () { return compute(cart); });
  }

  /* ---------- State ---------- */
  function compute(cart) {
    var items = cart.items || [];
    var boxes = [], addons = [];
    items.forEach(function (it) {
      var c = classify(it) || { box: false, addon: false };
      if (c.box) boxes.push(it);
      if (c.addon) addons.push(it);
    });
    var subtotal = Number(cart.total_price) || 0;              /* after discounts, before shipping */
    var grams = Number(cart.total_weight) || 0;
    /* Exactly 10.00 kg still qualifies for lockers — only STRICTLY over 10 kg removes them. */
    var heavy = grams > cfg.heavyGrams;
    var ship;
    if (subtotal >= cfg.freeAllFrom) {
      /* Lockers never survive at/above the free-for-everything threshold, regardless of weight. */
      ship = { code: 4, target: cfg.freeAllFrom, remaining: 0, progress: 100, complete: true, lockerAvailable: false, courierPrice: 0, lockerPrice: 0,
        title: T.state4, lines: [] };
    } else if (heavy) {
      ship = { code: 3, target: cfg.freeAllFrom, remaining: cfg.freeAllFrom - subtotal, progress: pct(subtotal, cfg.freeAllFrom), complete: false, lockerAvailable: false, courierPrice: cfg.courierPrice, lockerPrice: null,
        title: T.state3, lines: [T.state3courier.replace('{price}', money(cfg.courierPrice)), T.state3more.replace('{amount}', money(cfg.freeAllFrom - subtotal))] };
    } else if (subtotal >= cfg.lockerFreeFrom) {
      ship = { code: 2, target: cfg.freeAllFrom, remaining: cfg.freeAllFrom - subtotal, progress: pct(subtotal, cfg.freeAllFrom), complete: true, lockerAvailable: true, courierPrice: cfg.courierPrice, lockerPrice: 0,
        title: T.state2, lines: [T.state2courier.replace('{price}', money(cfg.courierPrice)), T.state2more.replace('{amount}', money(cfg.freeAllFrom - subtotal))] };
    } else {
      ship = { code: 1, target: cfg.lockerFreeFrom, remaining: cfg.lockerFreeFrom - subtotal, progress: pct(subtotal, cfg.lockerFreeFrom), complete: false, lockerAvailable: true, courierPrice: cfg.courierPrice, lockerPrice: cfg.lockerPrice,
        title: T.state1.replace('{amount}', money(cfg.lockerFreeFrom - subtotal)), lines: [] };
    }
    return {
      subtotal: subtotal, grams: grams, weight: fmtWeight(grams), heavy: heavy,
      hasBox: boxes.length > 0, boxes: boxes, addons: addons,
      invalidAddons: boxes.length === 0 && addons.length > 0,
      shipping: ship, notice: lastNotice, itemCount: cart.item_count || 0,
      requiresShipping: cart.requires_shipping !== false
    };
  }
  function pct(a, b) { return b > 0 ? Math.max(0, Math.min(100, Math.round(a / b * 100))) : 0; }

  /* ---------- Free-shipping progress view (compact; two milestones on one track) ----------
     Pure presentation of the numbers compute() produced. Eligibility always uses the order total
     after discounts (cart.total_price), thresholds come from theme settings (cfg), and the weight
     rule is the one compute() applies: a cart STRICTLY heavier than the heavy limit has no locker
     stage (courier only) until the free-for-all threshold.
     Track: two equal stages, 0 -> locker threshold (milestone at 50 %), locker -> free-for-all (100 %).
     A heavy cart shows a single courier stage with no locker milestone.
     renderShip(state, { weight: bool, weightLabel: str }) returns the module plus, optionally, one small
     weight line. Styles and size tokens: assets/isd-cart-ship.css */
  function fmtThreshold(c) { return money(c).replace(/[.,]00(?=\D|$)/, ''); }
  function h(s) { var d = document.createElement('div'); d.textContent = s == null ? '' : String(s); return d.innerHTML; }
  function tpl(str, map, bold) {
    var s = h(str);
    Object.keys(map).forEach(function (k) {
      var v = h(map[k]);
      s = s.split('{' + k + '}').join(bold && bold.indexOf(k) > -1 ? '<b>' + v + '</b>' : v);
    });
    return s;
  }
  var ICON_TICK = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6L9 17l-5-5"/></svg>';
  var ICON_OK = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9.5"/><path d="M7.8 12.4l3 3 5.4-6"/></svg>';
  var ICON_SCALE = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 3v18M5 21h14M3 9l4-6 4 6M13 9l4-6 4 6"/><path d="M3 9a4 4 0 0 0 8 0M13 9a4 4 0 0 0 8 0"/></svg>';
  var lastFill = 0;      /* previous bar fill (%), so a re-render animates from the old value instead of from 0 */

  function shipLabels() {
    return {
      locker: (htmlLang === 'lt' && text.lockerLabel) ? text.lockerLabel : SHIP.labelLocker,
      courier: (htmlLang === 'lt' && text.courierLabel) ? text.courierLabel : SHIP.labelCourier
    };
  }
  /* One model for the copy and the picture, so the screen-reader text can never disagree with the bar. */
  function shipModel(state) {
    var sub = Number(state.subtotal) || 0, L = cfg.lockerFreeFrom, F = cfg.freeAllFrom;
    var courierDone = sub >= F;
    var heavy = !!state.heavy && !courierDone;
    var lockerDone = courierDone || (!heavy && sub >= L);
    var staged = !heavy && L > 0 && L < F;              /* locker milestone shown at 50 % of the track */
    var fill;
    if (courierDone) fill = 100;
    else if (staged) fill = sub >= L ? 50 + (sub - L) / (F - L) * 50 : sub / L * 50;
    else fill = F > 0 ? sub / F * 100 : 0;
    if (!courierDone) fill = Math.min(99.4, fill);      /* never look "full" before the courier stage is reached */
    fill = Math.max(0, Math.round(fill * 10) / 10);
    if (sub > 0 && fill < 2.5) fill = 2.5;
    var stage = courierDone ? 'free' : heavy ? 'heavy' : lockerDone ? 'courier-next' : 'locker-next';
    var status = '', next = '';
    if (courierDone) status = SHIP.courierOpen;
    else if (heavy) { status = SHIP.heavyOnly; next = SHIP.toCourier; }
    else if (lockerDone) { status = SHIP.lockerOpen; next = SHIP.toCourier; }
    else next = SHIP.toLocker;
    var amount = money(Math.max(0, courierDone ? 0 : (lockerDone || heavy) ? F - sub : L - sub));
    return { sub: sub, L: L, F: F, courierDone: courierDone, heavy: heavy, lockerDone: lockerDone, staged: staged, fill: fill, stage: stage, status: status, next: next, amount: amount };
  }
  function plainOf(html) { return String(html).replace(/<[^>]+>/g, '').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#39;/g, "'"); }
  function shipParts(m) {
    var kg = fmtWeight(cfg.heavyGrams);
    return {
      status: m.status ? tpl(m.status, { kg: kg }) : '',
      next: m.next ? tpl(m.next, { amount: m.amount }, ['amount']) : ''
    };
  }
  /* Plain-text version of the message (status line + next step), for live regions and tests. */
  function shipText(state) {
    if (!state || !state.itemCount || state.requiresShipping === false) return '';
    var p = shipParts(shipModel(state));
    return plainOf((p.status + (p.status && p.next ? '. ' : '') + p.next).trim());
  }

  function renderShip(state, opts) {
    if (!state || !state.itemCount || state.requiresShipping === false) return '';
    opts = opts || {};
    var m = shipModel(state), p = shipParts(m), lab = shipLabels();
    var plain = plainOf((p.status + (p.status && p.next ? '. ' : '') + p.next).trim());
    var msg = (p.status ? '<span class="isd-ship__ok">' + (m.heavy ? ICON_SCALE : ICON_OK) + '<span>' + p.status + '</span></span>' : '')
      + (p.next ? '<span class="isd-ship__next">' + p.next + '</span>' : '');
    var from = lastFill; lastFill = m.fill;
    var lockerCls = m.lockerDone ? ' is-done' : '';
    var courierCls = m.courierDone ? ' is-done' : '';
    var html = '<div class="isd-ship is-' + m.stage + '" data-isd-ship-stage="' + m.stage + '">'
      + '<p class="isd-ship__msg" id="isd-ship-msg">' + msg + '</p>'
      + '<div class="isd-ship__rail" role="progressbar" aria-labelledby="isd-ship-msg" aria-valuemin="0" aria-valuemax="' + Math.round(m.F) / 100 + '" aria-valuenow="' + Math.round(Math.min(m.sub, m.F)) / 100 + '" aria-valuetext="' + h(plain) + '">'
      + '<span class="isd-ship__bar"><span class="isd-ship__fill" style="width:' + m.fill + '%;--isd-from:' + from + '%"></span></span>'
      + (m.staged ? '<span class="isd-ship__mark isd-ship__mark--locker' + lockerCls + '" style="left:50%">' + ICON_TICK + '</span>' : '')
      + '<span class="isd-ship__mark isd-ship__mark--courier' + courierCls + '" style="left:100%">' + ICON_TICK + '</span>'
      + '</div>'
      + '<div class="isd-ship__labels" aria-hidden="true">'
      + (m.staged ? '<span class="isd-ship__label isd-ship__label--locker' + lockerCls + '" style="left:50%"><i>' + h(lab.locker) + '</i> <b>' + h(fmtThreshold(m.L)) + '</b></span>' : '')
      + '<span class="isd-ship__label isd-ship__label--courier' + courierCls + '"><i>' + h(lab.courier) + '</i> <b>' + h(fmtThreshold(m.F)) + '</b></span>'
      + '</div></div>';
    if (opts.weight && Number(state.grams) > 0) {
      html += '<p class="isd-ship__weight' + (state.heavy ? ' is-heavy' : '') + '">' + ICON_SCALE + '<span>' + h(opts.weightLabel || SHIP.weight) + ' <b>' + h(state.weight) + '</b></span></p>';
    }
    return html;
  }

  /* ---------- Storefront visibility rule (JS twin of snippets/isd-disc-kind.liquid) ----------
     HIDDEN = tag "Produktų kolekcija" (the tasting sets), OR type "Dėžutės" whose handle is not one of
     the three prepared boxes. Works with products.json, predictive-search JSON, cart items and PDP data. */
  var BOX_HANDLES = ['italiskas-seimos-stalas', 'italiska-picerija-namuose', 'greitu-italisku-vakarieniu-rinkinys'];
  function productKind(p) {
    p = p || {};
    var type = norm(p.type || p.product_type);
    var tags = p.tags || [];
    if (typeof tags === 'string') tags = tags.split(',');
    tags = tags.map(norm);
    if (tags.indexOf('produktu kolekcija') > -1 || tags.indexOf('search-hidden') > -1) return 'hidden';
    if (type === 'dezutes') return BOX_HANDLES.indexOf(String(p.handle || '').toLowerCase()) > -1 ? 'box' : 'hidden';
    if (tags.indexOf('specialus pasiulymai') > -1) return 'offer';
    if (type === 'dovanu kuponai' || p.gift_card === true) return 'gift';
    return 'product';
  }

  /* ---------- Enforcement ---------- */
  var lastState = null, lastNotice = '', enforcing = false, listeners = [];
  function emit(state, cart, action) {
    lastState = state;
    listeners.slice().forEach(function (fn) { try { fn(state, cart); } catch (e) { console.warn('[ISD] state listener failed', e); } });
    document.dispatchEvent(new CustomEvent('isd:state', { detail: { state: state, cart: cart, action: action || 'refresh' } }));
    return state;
  }
  function removeAddons(state) {
    /* Remove every add-on line, one request at a time; the cart API emits after each change,
       the enforcing flag keeps those intermediate emits from re-entering here. */
    enforcing = true;
    var keys = state.addons.map(function (i) { return i.key; });
    var chain = Promise.resolve();
    keys.forEach(function (key) { chain = chain.then(function () { return window.ISD.cart.change(key, 0); }); });
    return chain.then(function (cart) { lastNotice = T.addonRemoved; enforcing = false; return cart || window.ISD.cart.get(); })
      .catch(function () { enforcing = false; return window.ISD.cart.get(); });
  }
  function handle(cart, detail) {
    if (enforcing) return;
    var action = detail && detail.action;
    resolve(cart).then(function (state) {
      if (state.invalidAddons) {
        return removeAddons(state).then(function (c2) { return resolve(c2).then(function (s2) { s2.notice = T.addonRemoved; emit(s2, c2, 'enforce'); }); });
      }
      if (action === 'add' || action === 'change') lastNotice = '';
      state.notice = lastNotice;
      emit(state, cart, action);
    });
  }
  window.ISD.cart.subscribe(handle);

  function validate() {
    return window.ISD.cart.get().then(function (cart) {
      return resolve(cart).then(function (state) {
        if (!state.invalidAddons) return state;
        return removeAddons(state).then(function (c2) { return resolve(c2).then(function (s2) { s2.notice = T.addonRemoved; return emit(s2, c2, 'enforce'); }); });
      });
    });
  }

  /* Seed from the server-rendered cart so the first paint needs no request. */
  function seedState() {
    if (lastState) return;
    var seed = document.querySelector('[data-isd-cart-seed]');
    if (seed) { try { var c = JSON.parse(seed.textContent); if (c && c.items) { c.item_count = c.item_count || 0; handle(c, { action: 'seed' }); return; } } catch (e) {} }
    /* No server seed in the DOM yet (it lives in the header block): fall back to one cart request. */
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } }).then(function (r) { return r.ok ? r.json() : null; }).then(function (c) { if (c && c.items && !lastState) handle(c, { action: 'seed' }); }).catch(function () {});
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', seedState); else seedState();

  window.ISD.state = {
    cfg: cfg, text: T, compute: compute, renderShip: renderShip, shipText: shipText, fmtThreshold: fmtThreshold, resolve: resolve, validate: validate,
    subscribe: function (fn) { listeners.push(fn); if (lastState) { try { fn(lastState, window.ISD.cart.last()); } catch (e) {} } return function () { listeners = listeners.filter(function (x) { return x !== fn; }); }; },
    last: function () { return lastState; },
    fmtWeight: fmtWeight,
    clearNotice: function () { lastNotice = ''; }
  };
  window.ISD.visibility = { kind: productKind, isHidden: function (p) { return productKind(p) === 'hidden'; }, boxHandles: BOX_HANDLES };

  /* Header cart icon opens a drawer with its own empty/filled cart state (data-sk-open="cart").
     While already on the /cart page that duplicates the page's own cart content, so stop the
     header's click handler (capture phase, before it reacts) and just no-op the click instead. */
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest ? e.target.closest('[data-sk-open="cart"]') : null;
    if (!trigger) return;
    var cartPath = (window.routes && window.routes.cart_url) || '/cart';
    var here = window.location.pathname.replace(/\/$/, '');
    if (here === cartPath.replace(/\/$/, '')) {
      e.preventDefault();
      e.stopImmediatePropagation();
    }
  }, true);
})();
